import React, {
  createContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useSelector } from 'react-redux';
import { produce } from 'immer';
import _, { get, has, set } from 'lodash';
import { BlackToastTrigger } from '@bbkAdminComponents/alerts/black-toast';
import { useLanguageSwitcherLocales } from '@bbkAdminComponents/designer/use-language-switcher-locales';
import { collectForwards } from '@bbkAdminComponents/experiences/offers/collect-forwards';
import { generateNewIdentityForOfferOrLossAversionCard } from '@bbkAdminComponents/experiences/offers/edit-offer-utils';
import { selectApp, selectCompany } from '@bbkAdminRedux/app/selectors';
import { type Integration } from '@bbkAdminRedux/data-management/reducers';
import { type BillingConfig } from '@bbkAdminRedux/rtkq/billing-config.slice';
import { useUploadBlobsToS3 } from '@bbkAdminRedux/rtkq/cdnAssets.slice';
import {
  useSelectAllFunnels,
  type FunnelType,
} from '@bbkAdminRedux/rtkq/funnels.slice';
import {
  genaiSlice,
  type EnrichArgs,
  type GenAIGeneratedContent,
} from '@bbkAdminRedux/rtkq/genai.slice';
import {
  offerConfigSlice,
  type OfferConfig,
} from '@bbkAdminRedux/rtkq/offer-config.slice';
import { offersAndLossAversionsSlice } from '@bbkAdminRedux/rtkq/offers-and-lossAversions.slice';
import {
  placementsSlice,
  useFetchWarningsForContent,
} from '@bbkAdminRedux/rtkq/placements.slice';
import { type FunnelPlacements } from '@bbkAdminRedux/rtkq/placements.utils';
import {
  parseRTKAPIError,
  type RTKAPIErrorResponse,
} from '@bbkAdminRedux/rtkq/rtkq-error-logger';
import { encodeObjectForQuery } from '@bbkAdminRedux/rtkq/rtkq-utils';
import { isLossAversionCard } from '@bbkAdminUtils/api-client/rest-cards-types';
import {
  isNewOffer,
  PublishState,
  type NewOffer,
  type RestOffer,
} from '@bbkAdminUtils/api-client/rest-offers-api';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import { usePendoTrack, type TrackPayload } from '@bbkAdminUtils/usePendo';
import { useSearchParams } from '@bbkAdminUtils/useSearchParams';
import { type Dictionary } from '@bbkAdminUtils/utility-types';
import { useHistory, useLocation } from '@bbkShared/react-router';

/** Session caches: draft survives Accept-step remounts; locale map for ML switch. */
type OfferLocaleSessionEntry = {
  offer: RestOffer | NewOffer;
  baseline: RestOffer | NewOffer;
  isPristine: boolean;
  selectedLocale?: string;
};

const offerEditorDraftById = new Map<string, OfferLocaleSessionEntry>();
const offerLocaleSessionCache = new Map<string, OfferLocaleSessionEntry>();
/** Delayed clear on unmount; cancelled if the same offer remounts (Accept step). */
const pendingSessionClears = new Map<string, ReturnType<typeof setTimeout>>();
const SESSION_CLEAR_DELAY_MS = 50;

const offerDraftKey = (appKey: string, offerId: string) =>
  `${appKey}:${offerId}`;

const offerLocaleSessionKey = (
  appKey: string,
  offerId: string,
  locale: string
) => `${appKey}:${offerId}:${locale}`;

const getOfferLocaleSession = (
  appKey: string,
  offerId: string,
  locale: string
) =>
  offerLocaleSessionCache.get(offerLocaleSessionKey(appKey, offerId, locale));

const setOfferLocaleSession = (
  appKey: string,
  offerId: string,
  locale: string,
  entry: OfferLocaleSessionEntry
) => {
  offerLocaleSessionCache.set(offerLocaleSessionKey(appKey, offerId, locale), {
    ...entry,
    selectedLocale: locale,
  });
};

const setOfferEditorDraft = (
  appKey: string,
  offerId: string,
  entry: OfferLocaleSessionEntry
) => {
  offerEditorDraftById.set(offerDraftKey(appKey, offerId), entry);
  if (entry.selectedLocale) {
    offerLocaleSessionCache.set(
      offerLocaleSessionKey(appKey, offerId, entry.selectedLocale),
      entry
    );
  }
};

const getOfferEditorDraft = (appKey: string, offerId: string) =>
  offerEditorDraftById.get(offerDraftKey(appKey, offerId));

const clearOfferLocaleSessionsForOffer = (appKey: string, offerId: string) => {
  const draftKey = offerDraftKey(appKey, offerId);
  offerEditorDraftById.delete(draftKey);
  const prefix = `${draftKey}:`;
  for (const key of [...offerLocaleSessionCache.keys()]) {
    if (key.startsWith(prefix)) offerLocaleSessionCache.delete(key);
  }
};

const cancelScheduledSessionClear = (appKey: string, offerId: string) => {
  const key = offerDraftKey(appKey, offerId);
  const timer = pendingSessionClears.get(key);
  if (timer === undefined) return;
  clearTimeout(timer);
  pendingSessionClears.delete(key);
};

const scheduleSessionClear = (appKey: string, offerId: string) => {
  cancelScheduledSessionClear(appKey, offerId);
  const key = offerDraftKey(appKey, offerId);
  pendingSessionClears.set(
    key,
    setTimeout(() => {
      pendingSessionClears.delete(key);
      clearOfferLocaleSessionsForOffer(appKey, offerId);
    }, SESSION_CLEAR_DELAY_MS)
  );
};

export const EditOfferPageContext = createContext<{
  // Offer
  offer: RestOffer | NewOffer;
  setOffer: React.Dispatch<React.SetStateAction<RestOffer | NewOffer>>;
  initOffer: RestOffer | NewOffer;

  // Billing Integration
  billingIntegration: Integration | undefined;

  // Billing Config
  billingConfig: BillingConfig;

  // Offer Config
  offerConfig: OfferConfig;
  setOfferConfig: React.Dispatch<React.SetStateAction<OfferConfig>>;

  // Placements
  placementsMap: Dictionary<FunnelPlacements>;
  setPlacementsMap: React.Dispatch<
    React.SetStateAction<Dictionary<FunnelPlacements>>
  >;

  //AI-specific settings
  editedTitle: boolean;
  setEditedTitle: React.Dispatch<React.SetStateAction<boolean>>;
  visitedOfferEditorHome: boolean;
  setVisitedOfferEditorHome: React.Dispatch<React.SetStateAction<boolean>>;
  aiContentGenerated: boolean;
  setAiContentGenerated: React.Dispatch<React.SetStateAction<boolean>>;

  // Localization
  selectedLocale: string | undefined;
  setSelectedLocale: (locale: string) => void;
  /** Switch locale, or prompt save/discard if dirty. */
  requestLocaleChange: (locale: string) => void;
  pendingLocale: string | undefined;
  clearPendingLocale: () => void;
  /** Secondary locale being edited; undefined on primary / flag-off. */
  viewLocale: string | undefined;
  isLocaleLoading: boolean;
  /** Primary snapshot for Accept-step structure in translation view. */
  primaryOffer: RestOffer | NewOffer;

  // Misc
  isPristine: boolean;
  reset: () => void;
  /** Clear session caches on discard/exit (not Accept-step remount). */
  clearEditorSession: () => void;
  previewDevice: PreviewDevice;
  setPreviewDevice: React.Dispatch<React.SetStateAction<PreviewDevice>>;

  // Async functions
  save: (
    publishState: PublishState,
    comment: string,
    checkWarnings: boolean
  ) => Promise<unknown>;
}>(undefined!);

type EditOfferPageProviderProps = {
  offer: RestOffer | NewOffer;
  billingConfig: BillingConfig;
  offerConfig: OfferConfig;
  placementsMap: Dictionary<FunnelPlacements>;
  billingIntegration: Integration | undefined;
  children: ReactNode;
  offerFunnelType: FunnelType;
};

export enum PreviewDevice {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  DESKTOP = 'desktop',
}

export enum MobileLayout {
  TAKEOVER_MODAL = 'takeover_modal',
  PARTIAL_MODAL = 'partial_modal',
}

export const EditOfferPageProvider: React.FC<EditOfferPageProviderProps> = ({
  offer: initOffer,
  billingConfig,
  offerConfig: initOfferConfig,
  placementsMap: initPlacementsMap,
  billingIntegration,
  children,
  offerFunnelType,
}) => {
  const company = useSelector(selectCompany);
  const companyId = company.internal_name;
  const app = useSelector(selectApp);
  const appKey = app.encoded_id;
  const activeFunnels = useSelectAllFunnels({
    appKey,
    funnelType: offerFunnelType,
  });
  const [createOffer] =
    offersAndLossAversionsSlice.endpoints.createOffer.useMutation();
  const [updateOffer] =
    offersAndLossAversionsSlice.endpoints.updateOffer.useMutation();
  const [fetchOfferForLocale] =
    offersAndLossAversionsSlice.endpoints.getOffer.useLazyQuery();
  const [updatePlacement] =
    placementsSlice.endpoints.updatePlacement.useMutation();
  const [putOfferConfig] =
    offerConfigSlice.endpoints.putOfferConfig.useMutation();
  const [enrichModelGeneratedContent] =
    genaiSlice.endpoints.enrich.useMutation();
  const [getModelGeneratedContent] =
    genaiSlice.endpoints.getContent.useLazyQuery();

  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>(
    PreviewDevice.DESKTOP
  );

  const uploadBlobsOnS3 = useUploadBlobsToS3();
  const fetchWarningsForContent = useFetchWarningsForContent();

  const pendoTrack = usePendoTrack();

  const history = useHistory();
  const location = useLocation();
  const searchParams = useSearchParams<{
    returnUrl?: string;
    locale?: string;
  }>();
  const localeFromUrl =
    typeof searchParams.locale === 'string' ? searchParams.locale : undefined;

  // Mount-time snapshot only — do not re-read the mutable Map each render.
  const restoredDraftRef = useRef(
    !isNewOffer(initOffer)
      ? getOfferEditorDraft(appKey, initOffer.id)
      : undefined
  );
  const restoredDraft = restoredDraftRef.current;

  const [isPristine, setIsPristineState] = useState<boolean>(
    restoredDraft?.isPristine ?? true
  );
  // Sync ref for save-then-switch in the same tick.
  const isPristineRef = useRef(isPristine);
  const setIsPristine = (next: boolean) => {
    isPristineRef.current = next;
    setIsPristineState(next);
  };

  // Offer
  const [offer, setOfferState] = useState(restoredDraft?.offer ?? initOffer);
  // Sync mirror for same-tick reset()/locale-switch.
  const offerRef = useRef(offer);
  offerRef.current = offer;

  // Localization — baseline is last loaded/saved snapshot for reset().
  const [baselineOffer, setBaselineOffer] = useState(
    restoredDraft?.baseline ?? initOffer
  );
  const baselineOfferRef = useRef(baselineOffer);
  baselineOfferRef.current = baselineOffer;
  const [primaryOffer, setPrimaryOffer] = useState(initOffer);
  const isMultiLanguage = useFeatureFlags(FeatureFlagsE.MULTI_LANGUAGE);
  const languageLocales = useLanguageSwitcherLocales({ enabledOnly: true });
  const primaryLocaleCode = useMemo(
    () => languageLocales.find((l) => l.isPrimary)?.code,
    [languageLocales]
  );

  const [selectedLocale, setSelectedLocaleState] = useState<string | undefined>(
    () => {
      if (restoredDraft?.selectedLocale) return restoredDraft.selectedLocale;
      if (
        localeFromUrl &&
        primaryLocaleCode &&
        localeFromUrl !== primaryLocaleCode
      ) {
        return localeFromUrl;
      }
      return undefined;
    }
  );
  const selectedLocaleRef = useRef(selectedLocale);
  selectedLocaleRef.current = selectedLocale;
  const [isLocaleLoading, setIsLocaleLoading] = useState(false);
  const localeRequestRef = useRef(0);
  const cachedOfferIdRef = useRef(initOffer.id);
  const didInitLocaleRef = useRef(!!restoredDraft?.selectedLocale);
  const viewLocale =
    isMultiLanguage && selectedLocale && selectedLocale !== primaryLocaleCode
      ? selectedLocale
      : undefined;

  // Cancel pending leave-clear when remounting (Accept step); schedule clear on leave.
  useEffect(() => {
    cancelScheduledSessionClear(appKey, initOffer.id);
    return () => {
      scheduleSessionClear(appKey, initOffer.id);
    };
  }, [appKey, initOffer.id]);

  // One-shot: hydrate primary structure from session after locales load.
  const didHydratePrimaryRef = useRef(false);
  useEffect(() => {
    if (didHydratePrimaryRef.current || !primaryLocaleCode) return;
    const locale = restoredDraftRef.current?.selectedLocale;
    if (!locale || locale === primaryLocaleCode) return;
    didHydratePrimaryRef.current = true;
    const primaryCached = getOfferLocaleSession(
      appKey,
      initOffer.id,
      primaryLocaleCode
    );
    if (primaryCached) setPrimaryOffer(primaryCached.offer);
  }, [appKey, primaryLocaleCode, initOffer.id]);

  const persistDraft = (
    nextOffer: RestOffer | NewOffer,
    nextBaseline: RestOffer | NewOffer,
    nextPristine: boolean,
    locale?: string
  ) => {
    if (isNewOffer(initOffer)) return;
    setOfferEditorDraft(appKey, initOffer.id, {
      offer: nextOffer,
      baseline: nextBaseline,
      isPristine: nextPristine,
      selectedLocale: locale ?? selectedLocaleRef.current,
    });
  };

  const setOffer: typeof setOfferState = (update) => {
    setIsPristine(false);
    const prev = offerRef.current;
    const next =
      typeof update === 'function'
        ? (update as (p: typeof prev) => typeof prev)(prev)
        : update;
    offerRef.current = next;
    // Sync write so Accept-step navigation before the effect still keeps edits.
    persistDraft(next, baselineOfferRef.current, false);
    setOfferState(next);
  };

  useEffect(() => {
    if (cachedOfferIdRef.current === initOffer.id) return;
    clearOfferLocaleSessionsForOffer(appKey, cachedOfferIdRef.current);
    cachedOfferIdRef.current = initOffer.id;
  }, [appKey, initOffer.id]);

  useEffect(() => {
    if (isNewOffer(initOffer)) return;
    persistDraft(offer, baselineOffer, isPristine, selectedLocale);
    if (selectedLocale) {
      setOfferLocaleSession(appKey, initOffer.id, selectedLocale, {
        offer,
        baseline: baselineOffer,
        isPristine,
      });
    }
  }, [offer, baselineOffer, isPristine, selectedLocale, initOffer, appKey]);

  // Don't let primary initOffer refetch overwrite a dirty translation/draft.
  useEffect(() => {
    if (!selectedLocale) return;
    if (viewLocale) return;
    const draft = getOfferEditorDraft(appKey, initOffer.id);
    if (draft && !draft.isPristine) return;
    if (!isPristine && offer !== initOffer) return;
    setBaselineOffer(initOffer);
    setPrimaryOffer(initOffer);
    if (!isNewOffer(initOffer) && primaryLocaleCode) {
      setOfferLocaleSession(appKey, initOffer.id, primaryLocaleCode, {
        offer: initOffer,
        baseline: initOffer,
        isPristine: true,
      });
    }
  }, [
    initOffer,
    viewLocale,
    primaryLocaleCode,
    selectedLocale,
    isPristine,
    offer,
    appKey,
  ]);

  const syncLocaleQueryParam = (locale: string) => {
    if (!isMultiLanguage || !primaryLocaleCode) return;
    const nextLocale = locale === primaryLocaleCode ? undefined : locale;
    if (localeFromUrl === nextLocale) return;
    history.replace({
      pathname: location.pathname,
      search: encodeObjectForQuery({
        returnUrl: searchParams.returnUrl,
        locale: nextLocale,
      }),
      state: location.state,
    });
  };

  const hydratePrimaryOfferFromSession = () => {
    if (!primaryLocaleCode) return;
    const primaryCached = getOfferLocaleSession(
      appKey,
      initOffer.id,
      primaryLocaleCode
    );
    if (primaryCached) setPrimaryOffer(primaryCached.offer);
  };

  const applyLocaleOffer = (
    locale: string,
    loaded: RestOffer,
    isBase: boolean,
    options?: { baseline?: RestOffer | NewOffer; isPristine?: boolean }
  ) => {
    const baseline = options?.baseline ?? loaded;
    const nextPristine = options?.isPristine ?? true;
    setOfferLocaleSession(appKey, initOffer.id, locale, {
      offer: loaded,
      baseline,
      isPristine: nextPristine,
    });
    setOfferEditorDraft(appKey, initOffer.id, {
      offer: loaded,
      baseline,
      isPristine: nextPristine,
      selectedLocale: locale,
    });
    setBaselineOffer(baseline);
    offerRef.current = loaded;
    setOfferState(loaded);
    if (isBase) {
      setPrimaryOffer(loaded);
    } else {
      hydratePrimaryOfferFromSession();
    }
    setIsPristine(nextPristine);
    setSelectedLocaleState(locale);
    syncLocaleQueryParam(locale);
  };

  const setSelectedLocale = (locale: string) => {
    if (!isMultiLanguage) return;
    if (locale === selectedLocale) return;
    const isBase = !locale || locale === primaryLocaleCode;
    const leavingPristine = isPristineRef.current;

    // Cache leaving locale from offerRef (handles same-tick discard → switch).
    const leavingOffer = offerRef.current;
    if (selectedLocale) {
      setOfferLocaleSession(appKey, initOffer.id, selectedLocale, {
        offer: leavingOffer,
        baseline: baselineOfferRef.current,
        isPristine: leavingPristine,
      });
      persistDraft(
        leavingOffer,
        baselineOfferRef.current,
        leavingPristine,
        selectedLocale
      );
      if (!viewLocale && primaryLocaleCode) {
        setPrimaryOffer(leavingOffer);
      }
    }

    if (isNewOffer(initOffer)) {
      setSelectedLocaleState(locale);
      syncLocaleQueryParam(locale);
      return;
    }

    const cacheKey = isBase ? primaryLocaleCode : locale;
    if (cacheKey) {
      const cached = getOfferLocaleSession(appKey, initOffer.id, cacheKey);
      if (cached) {
        applyLocaleOffer(locale, cached.offer as RestOffer, isBase, {
          baseline: cached.baseline,
          isPristine: cached.isPristine,
        });
        return;
      }
    }

    const requestId = ++localeRequestRef.current;
    setIsLocaleLoading(true);
    fetchOfferForLocale(
      {
        appKey,
        offerId: initOffer.id,
        ...(isBase ? {} : { locale }),
      },
      true
    )
      .unwrap()
      .then((loaded) => {
        if (localeRequestRef.current !== requestId) return;
        applyLocaleOffer(locale, loaded, isBase);
      })
      .catch((e: RTKAPIErrorResponse) => {
        if (localeRequestRef.current !== requestId) return;
        const message = parseRTKAPIError(e).message;
        BlackToastTrigger({
          content: `Could not load ${locale} translation. ${message}`,
        });
        setSelectedLocaleState((prev) => prev ?? primaryLocaleCode);
      })
      .finally(() => {
        if (localeRequestRef.current !== requestId) return;
        setIsLocaleLoading(false);
      });
  };

  const [pendingLocale, setPendingLocale] = useState<string>();
  const clearPendingLocale = () => setPendingLocale(undefined);
  const requestLocaleChange = (locale: string) => {
    if (!isMultiLanguage) return;
    if (locale === selectedLocale) return;
    if (!isPristineRef.current) {
      setPendingLocale(locale);
      return;
    }
    setSelectedLocale(locale);
  };

  useEffect(() => {
    if (!primaryLocaleCode) return;
    if (!isMultiLanguage) {
      if (!selectedLocale) setSelectedLocaleState(primaryLocaleCode);
      return;
    }
    if (didInitLocaleRef.current) return;
    didInitLocaleRef.current = true;

    const draft = !isNewOffer(initOffer)
      ? getOfferEditorDraft(appKey, initOffer.id)
      : undefined;
    if (draft && !draft.isPristine) {
      const locale =
        draft.selectedLocale ??
        (localeFromUrl && localeFromUrl !== primaryLocaleCode
          ? localeFromUrl
          : primaryLocaleCode);
      setSelectedLocaleState(locale);
      if (locale !== primaryLocaleCode) hydratePrimaryOfferFromSession();
      return;
    }

    const targetLocale =
      localeFromUrl && localeFromUrl !== primaryLocaleCode
        ? localeFromUrl
        : primaryLocaleCode;
    const isBase = targetLocale === primaryLocaleCode;

    if (!isNewOffer(initOffer)) {
      const cached = getOfferLocaleSession(appKey, initOffer.id, targetLocale);
      if (cached) {
        applyLocaleOffer(targetLocale, cached.offer as RestOffer, isBase, {
          baseline: cached.baseline,
          isPristine: cached.isPristine,
        });
        return;
      }
    }

    if (!isBase) {
      setSelectedLocale(targetLocale);
      return;
    }
    setSelectedLocaleState(primaryLocaleCode);
  }, [primaryLocaleCode, localeFromUrl, isMultiLanguage, selectedLocale]);

  // Offer Config
  const [offerConfig, setOfferConfigState] =
    useState<OfferConfig>(initOfferConfig);
  useEffect(() => setOfferConfigState(initOfferConfig), [initOfferConfig]);
  const setOfferConfig: typeof setOfferConfigState = (...args) => {
    setIsPristine(false);
    setOfferConfigState(...args);
  };

  // Placements
  const [placementsMap, setPlacementsMapState] = useState(initPlacementsMap);
  const setPlacementsMap: typeof setPlacementsMapState = (...args) => {
    setIsPristine(false);
    setPlacementsMapState(...args);
  };

  //AI-specific settings
  const [editedTitle, setEditedTitle] = useState<boolean>(false);
  const [visitedOfferEditorHome, setVisitedOfferEditorHome] =
    useState<boolean>(false);
  const [aiContentGenerated, setAiContentGenerated] = useState<boolean>(false);

  // Misc
  const reset = () => {
    setIsPristine(true);
    offerRef.current = baselineOffer;
    setOfferState(baselineOffer);
    persistDraft(baselineOffer, baselineOffer, true, selectedLocale);
    if (selectedLocale) {
      setOfferLocaleSession(appKey, initOffer.id, selectedLocale, {
        offer: baselineOffer,
        baseline: baselineOffer,
        isPristine: true,
      });
    }
    if (!viewLocale && primaryLocaleCode) {
      setPrimaryOffer(baselineOffer);
    }
    setPlacementsMapState(initPlacementsMap);
  };

  const clearEditorSession = () => {
    cancelScheduledSessionClear(appKey, initOffer.id);
    clearOfferLocaleSessionsForOffer(appKey, initOffer.id);
  };

  const buildPendoTrackPayloadForAIOfferSave = (
    offer: RestOffer | NewOffer,
    outcome: EnrichArgs['outcome']
  ) =>
    ({
      event: `${isNewOffer(offer) ? 'Saving' : 'Updating'} AI offer${outcome === 'ACCEPTED_BUT_MODIFIED' ? ' with changes' : ''}`,
      properties: {
        offerId: offer.id,
        category: offer.category,
      },
    }) as TrackPayload;

  // Async functions
  const save = async (
    publishState: PublishState,
    comment: string,
    checkWarnings: boolean
  ) => {
    const updatedOffer = await uploadBlobsOnS3(offer);

    const activeFunnelsPlacementsMap: Dictionary<FunnelPlacements> = {};
    activeFunnels.forEach((funnel) => {
      if (funnel.active) {
        activeFunnelsPlacementsMap[funnel.id] = placementsMap[funnel.id];
      }
    });

    if (isMultiLanguage && viewLocale) {
      let primaryForPublish = primaryOffer;
      if (publishState === PublishState.live && !isNewOffer(initOffer)) {
        try {
          primaryForPublish = await fetchOfferForLocale({
            appKey,
            offerId: initOffer.id,
          }).unwrap();
          setPrimaryOffer(primaryForPublish);
          if (primaryLocaleCode) {
            setOfferLocaleSession(appKey, initOffer.id, primaryLocaleCode, {
              offer: primaryForPublish,
              baseline: primaryForPublish,
              isPristine: true,
            });
          }
        } catch (err) {
          const message = parseRTKAPIError(err as RTKAPIErrorResponse).message;
          return {
            type: 'error',
            message: `We could not load the primary offer for publish. Please try again. Error: ${message}`,
          };
        }
      }

      if (
        publishState === PublishState.live &&
        checkWarnings &&
        !isNewOffer(primaryForPublish)
      ) {
        const result = await fetchWarningsForContent(
          primaryForPublish,
          activeFunnelsPlacementsMap,
          offerFunnelType
        );
        if (result) {
          return { type: 'error', message: result };
        }
      }

      try {
        await updateOffer({
          appKey,
          publish_state: PublishState.draft,
          content: updatedOffer,
          comment,
          locale: viewLocale,
        }).unwrap();
        if (
          publishState === PublishState.live &&
          !isNewOffer(primaryForPublish)
        ) {
          await updateOffer({
            appKey,
            publish_state: PublishState.live,
            content: primaryForPublish,
            comment,
          }).unwrap();
        }
      } catch (err) {
        const message = parseRTKAPIError(err as RTKAPIErrorResponse).message;
        return {
          type: 'error',
          message: `We could not save the ${viewLocale} translation. Please try again. Error: ${message}`,
        };
      }
      offerRef.current = updatedOffer;
      setOfferState(updatedOffer);
      setBaselineOffer(updatedOffer);
      setIsPristine(true);
      persistDraft(updatedOffer, updatedOffer, true, viewLocale);
      setOfferLocaleSession(appKey, initOffer.id, viewLocale, {
        offer: updatedOffer,
        baseline: updatedOffer,
        isPristine: true,
      });
      return { offerId: updatedOffer.id };
    }

    let offerToSave = updatedOffer;
    let updatedOfferId = null;
    let placementToSave = activeFunnelsPlacementsMap;
    const isNew = isNewOffer(updatedOffer);
    if (isNew) {
      const prevId = updatedOffer.id;
      const { cards } = updatedOffer;
      const updateCards = cards.map((card) => {
        const newCardIdentity = generateNewIdentityForOfferOrLossAversionCard(
          offer,
          card
        );
        return {
          ...card,
          newCardData: newCardIdentity,
        };
      });

      const updatedOfferWithRemappedNames = produce(updatedOffer, (draft) => {
        const { cards: updatedOfferCards } = draft;

        updatedOfferCards.forEach((card, index) => {
          if (has(card, 'requires.forwards.modal')) {
            const prevModal = get(card, 'requires.forwards.modal');
            const cardUpdate = updateCards.find((uc) => uc.name === prevModal);
            if (!cardUpdate || !cardUpdate?.newCardData.name) {
              GlobalLogger.error(
                `Broken reference to the modal: can't find card with name ${prevModal} ${JSON.stringify(card)} ${JSON.stringify(updateCards)} ${JSON.stringify(cardUpdate)}`
              );
            }
            set(card, 'requires.forwards.modal', cardUpdate?.newCardData.name);
          }
          if ('generic_actions' in card.requires) {
            card.requires.generic_actions?.forEach((ga) => {
              if (has(ga, 'modal')) {
                const cardUpdate = updateCards.find(
                  (uc) => uc.name === get(ga, 'modal')
                );
                set(ga, 'modal', cardUpdate?.newCardData.name);
              }
            });
          }
          if ('other_actions' in card.requires) {
            card.requires.other_actions?.forEach((oa) => {
              if (has(oa, 'modal')) {
                const cardUpdate = updateCards.find(
                  (uc) => uc.name === get(oa, 'modal')
                );
                set(oa, 'modal', cardUpdate?.newCardData.name);
              }
            });
          }
          const ctas = collectForwards(card);
          ctas?.forEach((draftCta) => {
            const ctaIdPath = [
              'cards',
              index,
              ...draftCta.path,
              'cta_details',
              'cta_id',
            ];
            set(card, ctaIdPath, undefined);
          });

          set(card, 'id', updateCards[index]?.newCardData.id);
          set(card, 'name', updateCards[index]?.newCardData.name);
        });

        set(draft, 'primary_card_name', draft.cards[0]?.name);
        set(draft, 'id', undefined);
      });

      const createdOffer = await createOffer({
        appKey,
        offer: updatedOfferWithRemappedNames,
      })
        .unwrap()
        .catch((e: RTKAPIErrorResponse) => {
          throw e.data;
        });

      const newId = createdOffer.id;
      updatedOfferId = newId;
      // update placements
      const newPlacements = produce(activeFunnelsPlacementsMap, (draft) => {
        Object.entries(draft).forEach(([funnelId, el]) => {
          if (!el) return;
          el.placements.forEach((p) => {
            if (get(p, 'content_id') === prevId) {
              set(p, 'content_id', newId);
            }
          });
        });
      });

      setOffer(createdOffer);
      setPlacementsMap(newPlacements);
      offerToSave = createdOffer;
      placementToSave = newPlacements;
    }

    //TODO AbK if offer content was model generated, then update contentID and outcome in OfferContent table for chosen record - also reject any remaining unchosen records for offer
    const primaryCardIdx = offer.cards.findIndex(
      (c) => c.name === offer.primary_card_name
    );
    const offerContentIdPath = ['cards', primaryCardIdx, 'offer_content_id'];
    const offerContentId = _.get(offerToSave, offerContentIdPath);
    if (!!offerContentId) {
      await getModelGeneratedContent({
        companyKey: companyId,
        appKey,
        id: offerContentId,
      })
        .unwrap()
        //has model generateed content
        .then(async (offerContentRecord) => {
          if (!offerContentRecord) return;
          const { outcome: initialOutcome, model_output } = offerContentRecord;
          if (initialOutcome === 'ACCEPTED_BUT_MODIFIED') {
            pendoTrack?.(
              buildPendoTrackPayloadForAIOfferSave(updatedOffer, initialOutcome)
            );
            return;
          }
          const {
            title,
            description,
            cta_text: buttonText,
          } = JSON.parse(model_output) as GenAIGeneratedContent;
          const primaryCard = offer.cards.at(primaryCardIdx)!;
          let outcome: EnrichArgs['outcome'] = initialOutcome;

          if (isLossAversionCard(primaryCard)) {
            const cardTitle = _.get(primaryCard, ['requires', 'title']);
            const cardDescription = _.get(primaryCard, ['requires', 'copy']);
            const cardButtonText = _.get(primaryCard, [
              'requires',
              'forwards',
              'text',
            ]);
            if (
              title !== cardTitle ||
              description !== cardDescription ||
              buttonText !== cardButtonText
            ) {
              outcome = 'ACCEPTED_BUT_MODIFIED';
            }
          } else {
            const cardTitle = _.get(primaryCard, ['requires', 'info', 'title']);
            const cardDescription = _.get(primaryCard, [
              'requires',
              'info',
              'details',
            ]);
            const cardButtonText = _.get(primaryCard, [
              'requires',
              'forwards',
              'text',
            ]);
            if (
              title !== cardTitle ||
              description !== cardDescription ||
              buttonText !== cardButtonText
            ) {
              outcome = 'ACCEPTED_BUT_MODIFIED';
            }
          }

          pendoTrack?.(
            buildPendoTrackPayloadForAIOfferSave(updatedOffer, outcome)
          );
          if (!outcome || outcome !== initialOutcome) {
            await enrichModelGeneratedContent({
              companyKey: companyId,
              appKey,
              content_id: offerToSave.id,
              offer_content_id: offerContentId,
              outcome: outcome ?? 'ACCEPTED',
            }).unwrap();
          }
        });
    }

    if (publishState === PublishState.live && checkWarnings) {
      const result = await fetchWarningsForContent(
        offerToSave,
        placementToSave,
        offerFunnelType
      );
      if (result) {
        return { type: 'error', message: result };
      }
    }

    // check that billing is present for the case where two keys are necessary
    if (!!billingIntegration && billingConfig?.manage_offers) {
      await putOfferConfig({
        app_key: appKey,
        offer_id: offerToSave.id,
        publish_state: publishState,
        ...offerConfig,
      })
        .unwrap()
        .catch((e: RTKAPIErrorResponse) => {
          const message = parseRTKAPIError(e).message;
          BlackToastTrigger({
            content: `Could not update offer configs ${message}`,
          });
        });
    }

    const funnelPlacementsThatChanged = _.chain(placementToSave)
      .values()
      .filter((fp) => !_.isUndefined(fp))
      .filter((fp) => {
        // This filter is answering the question:
        // Do we need to save this FunnelPlacements
        // We are just diffing placement in original and resulting placements
        const originalContentPlacement = offerToSave.placements.filter(
          (x) => x.funnel_id === fp.id
        );
        const resultContentPlacement = fp.placements.filter(
          (p) => p.content_id === offerToSave.id
        );
        return !_.isEqual(originalContentPlacement, resultContentPlacement);
      })
      .value();

    await Promise.all(
      funnelPlacementsThatChanged.map((el) =>
        updatePlacement({
          appKey,
          funnelId: el.id,
          publish_state: publishState,
          placements: el.placements,
          comment,
        })
      )
    );

    try {
      await updateOffer({
        appKey,
        publish_state: publishState,
        content: offerToSave,
        comment,
      }).unwrap();
    } catch (err) {
      const message = parseRTKAPIError(err as RTKAPIErrorResponse).message;
      return {
        type: 'error',
        message: `We could not save the offer. Please try again. Error: ${message}`,
      };
    }

    setPrimaryOffer(offerToSave);
    offerRef.current = offerToSave;
    setOfferState(offerToSave);
    setBaselineOffer(offerToSave);
    setIsPristine(true);
    // Drop stale secondary caches; persist pristine draft before possible redirect.
    cancelScheduledSessionClear(appKey, initOffer.id);
    clearOfferLocaleSessionsForOffer(appKey, initOffer.id);
    persistDraft(offerToSave, offerToSave, true, primaryLocaleCode);
    if (primaryLocaleCode) {
      setOfferLocaleSession(appKey, initOffer.id, primaryLocaleCode, {
        offer: offerToSave,
        baseline: offerToSave,
        isPristine: true,
      });
    }
    return { offerId: updatedOfferId };
  };

  return (
    <EditOfferPageContext.Provider
      value={{
        // Offer
        offer,
        setOffer,
        previewDevice,
        setPreviewDevice,
        initOffer,

        // Billing Integration
        billingIntegration,

        // Billing Config
        billingConfig,

        // Offer Config
        offerConfig,
        setOfferConfig,

        // Placements
        placementsMap,
        setPlacementsMap,

        //AI-specific settings
        editedTitle,
        setEditedTitle,
        visitedOfferEditorHome,
        setVisitedOfferEditorHome,
        aiContentGenerated,
        setAiContentGenerated,

        // Localization
        selectedLocale,
        setSelectedLocale,
        requestLocaleChange,
        pendingLocale,
        clearPendingLocale,
        viewLocale,
        isLocaleLoading,
        primaryOffer,

        // Misc
        isPristine,
        reset,
        clearEditorSession,

        // Async functions
        save,
      }}
    >
      {children}
    </EditOfferPageContext.Provider>
  );
};
